import axios from 'axios';
import {getAPIUrl} from "./services/BaseService";
import {getAccessToken, getRefreshToken, setTokens, clearTokens} from "./utils/tokenStorage";

type RetryableAxiosRequest = {
    _retry?: boolean;
    headers?: Record<string, string>;
};

const REFRESH_TIMEOUT_MS = 10000;

const axiosClient = axios.create({
    baseURL: getAPIUrl(),
    timeout: 10000,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
    },
    responseType: 'json',
});

// Attach Bearer token to every request
axiosClient.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (token) {
        config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
});

// On 401: attempt token refresh once, then redirect to login
axiosClient.interceptors.response.use(
    response => response,
    async (error) => {
        const originalRequest = error?.config as RetryableAxiosRequest | undefined;

        if (!originalRequest) {
            return Promise.reject(error);
        }

        // ── Defence-in-depth: surface access-gate envelopes ───────────
        // The AccessGate component already covers the happy path by
        // reading /me, but if any background request hits a 402/403
        // envelope (e.g. quota check after a state transition) we
        // refresh /me once so AccessGate can re-evaluate. A short-lived
        // sentinel prevents reload loops if the server keeps returning
        // the same envelope.
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail;
        const code = typeof detail === 'object' ? detail?.code : undefined;
        const isAccessEnvelope =
            (status === 403 && code === 'WAITLISTED') ||
            (status === 402 && code === 'PAYMENT_METHOD_REQUIRED');
        if (isAccessEnvelope) {
            const sentinelKey = 'gradde.accessReloadAt';
            const lastReload = Number(sessionStorage.getItem(sentinelKey) || '0');
            const now = Date.now();
            if (
                window.location.pathname !== '/login' &&
                now - lastReload > 30000
            ) {
                sessionStorage.setItem(sentinelKey, String(now));
                window.location.reload();
            }
            return Promise.reject(error);
        }

        if (error.response && error.response.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            const refreshToken = getRefreshToken();
            if (refreshToken) {
                try {
                    const res = await axios.post(
                        getAPIUrl() + '/auth/token/refresh',
                        { refresh_token: refreshToken },
                        {
                            headers: { 'Content-Type': 'application/json' },
                            timeout: REFRESH_TIMEOUT_MS,
                        }
                    );
                    const { access_token, refresh_token } = res.data;
                    // Preserve the user's storage choice: if the existing
                    // refresh token lives in sessionStorage, keep the new
                    // tokens session-only too.
                    const persistent = localStorage.getItem('gradde_refresh_token') !== null;
                    setTokens(access_token, refresh_token, persistent);
                    originalRequest.headers = originalRequest.headers ?? {};
                    originalRequest.headers['Authorization'] = `Bearer ${access_token}`;
                    return axiosClient(originalRequest);
                } catch {
                    // Refresh failed — force re-login
                }
            }

            clearTokens();
            window.location.href = '/login';
        }

        return Promise.reject(error);
    }
);

export default axiosClient;
